import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hrService } from '../hrService';
import type { AttendanceQueryParams, LeaveQueryParams, SalaryAdvanceQueryParams } from '../hrService';

export function useShifts(activeOnly = false) {
  return useQuery({
    queryKey: ['shifts', activeOnly],
    queryFn: () => hrService.getShifts(activeOnly),
    staleTime: 30000,
  });
}

// Shift mutations show their own toasts in `ShiftManagement`, so the global
// MutationCache toast is suppressed to avoid duplicate notifications.
export function useCreateShift() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hrService.createShift,
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
  });
}

export function useUpdateShift() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => hrService.updateShift(id, data),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
}

export function useDeleteShift() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hrService.deleteShift(id),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
}

export function useAttendance(employeeId?: string, dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: ['attendance', employeeId, dateFrom, dateTo],
    queryFn: () => hrService.getAttendance(employeeId, dateFrom, dateTo),
    staleTime: 30000,
  });
}

/** Paginated attendance list (search/status filters). */
export function useAttendancePaginated(params: AttendanceQueryParams = {}) {
  return useQuery({
    queryKey: ['attendance', 'paginated', params],
    queryFn: () => hrService.getAttendancePaginated(params),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useCreateAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hrService.createAttendance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
}

export function useUpdateAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => hrService.updateAttendance(id, data),
    meta: { successMessage: 'Attendance updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
}

export function useDeleteAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hrService.deleteAttendance(id),
    meta: { successMessage: 'Attendance record deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
}

/** Bulk-delete attendance records in a single request. */
export function useBulkDeleteAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => hrService.bulkDeleteAttendance(ids),
    meta: { successMessage: 'Attendance records deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
}

export function useBulkAssignShift() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hrService.bulkAssignShift,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
}

export function useLeave(employeeId?: string, status?: string, dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: ['leave', employeeId, status, dateFrom, dateTo],
    queryFn: () => hrService.getLeave(employeeId, status, dateFrom, dateTo),
    staleTime: 30000,
  });
}

/** Paginated leave list (search/status filters). */
export function useLeavePaginated(params: LeaveQueryParams = {}) {
  return useQuery({
    queryKey: ['leave', 'paginated', params],
    queryFn: () => hrService.getLeavePaginated(params),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useCreateLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hrService.createLeave,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function useApproveLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => hrService.approveLeave(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function useUpdateLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => hrService.updateLeave(id, data),
    meta: { successMessage: 'Leave updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export function useDeleteLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hrService.deleteLeave(id),
    meta: { successMessage: 'Leave record deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

/** Bulk-delete leave records in a single request. */
export function useBulkDeleteLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => hrService.bulkDeleteLeave(ids),
    meta: { successMessage: 'Leave records deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
  });
}

export interface PayrollQueryParams {
  employeeId?: string;
  payPeriod?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function usePayroll(params: PayrollQueryParams = {}) {
  return useQuery({
    queryKey: ['payroll', params],
    queryFn: () => hrService.getPayroll(params),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function usePayrollRecord(id: string) {
  return useQuery({
    queryKey: ['payroll', 'record', id],
    queryFn: () => hrService.getPayrollById(id),
    enabled: !!id,
  });
}

export function useCreatePayroll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hrService.createPayroll,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

export function useUpdatePayroll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof hrService.updatePayroll>[1] }) =>
      hrService.updatePayroll(id, data),
    meta: { successMessage: 'Payroll record updated' },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payroll'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'record', variables.id] });
    },
  });
}

/** Generate payroll for every active employee in a pay period. */
export function useBulkGeneratePayroll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hrService.bulkGeneratePayroll,
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

/** Fetch a payroll payslip PDF and trigger a browser download. */
export function useDownloadPayslip() {
  return useMutation({
    mutationFn: async ({ id, fileName }: { id: string; fileName: string }) => {
      const blob = await hrService.downloadPayslip(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    meta: { successMessage: 'Payslip downloaded' },
  });
}

/**
 * Fetch a payroll payslip PDF and open the browser print dialog. The PDF is
 * rendered in a hidden iframe so the browser's native PDF viewer handles
 * paging and layout, falling back to a new tab when inline printing fails.
 */
export function usePrintPayslip() {
  return useMutation({
    mutationFn: async (id: string) => {
      const blob = await hrService.downloadPayslip(id);
      const url = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      iframe.src = url;
      document.body.appendChild(iframe);

      await new Promise<void>((resolve) => {
        iframe.onload = () => resolve();
        // The PDF viewer does not always fire `load`; don't hang the mutation.
        window.setTimeout(resolve, 1500);
      });

      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
        // Fallback: open the PDF so the user can print from the viewer.
        window.open(url, '_blank');
      }

      window.setTimeout(() => {
        iframe.remove();
        URL.revokeObjectURL(url);
      }, 60_000);
    },
    meta: { successMessage: 'Payslip ready to print' },
  });
}

/** Apply one status to many payroll records at once. */
export function useBulkUpdatePayrollStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hrService.bulkUpdatePayrollStatus,
    meta: { successMessage: 'Payroll status updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

export function useDeletePayroll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hrService.deletePayroll(id),
    meta: { successMessage: 'Payroll record deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

/** Bulk-delete payroll records in a single request. */
export function useBulkDeletePayroll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => hrService.bulkDeletePayroll(ids),
    meta: { successMessage: 'Payroll records deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

// ── Salary advances ────────────────────────────────────────────────────────
export function useSalaryAdvances(params: SalaryAdvanceQueryParams = {}) {
  return useQuery({
    queryKey: ['salary-advances', params],
    queryFn: () => hrService.getSalaryAdvances(params),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useSalaryAdvanceRecoveries(id: string) {
  return useQuery({
    queryKey: ['salary-advances', id, 'recoveries'],
    queryFn: () => hrService.getSalaryAdvanceRecoveries(id),
    enabled: !!id,
  });
}

export function useCreateSalaryAdvance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hrService.createSalaryAdvance,
    meta: { successMessage: 'Salary advance created' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salary-advances'] });
    },
  });
}

export function useUpdateSalaryAdvance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof hrService.updateSalaryAdvance>[1] }) =>
      hrService.updateSalaryAdvance(id, data),
    meta: { successMessage: 'Salary advance updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salary-advances'] });
    },
  });
}

export function useApproveSalaryAdvance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hrService.approveSalaryAdvance(id),
    meta: { successMessage: 'Salary advance approved' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salary-advances'] });
    },
  });
}

export function useDeleteSalaryAdvance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hrService.deleteSalaryAdvance(id),
    meta: { successMessage: 'Salary advance deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salary-advances'] });
    },
  });
}

// ── Payroll policy ─────────────────────────────────────────────────────────
export function usePayrollPolicies() {
  return useQuery({
    queryKey: ['payroll-policies'],
    queryFn: () => hrService.getPayrollPolicies(),
    staleTime: 30000,
  });
}

export function useEffectivePayrollPolicy(departmentId?: string) {
  return useQuery({
    queryKey: ['payroll-policies', 'effective', departmentId ?? null],
    queryFn: () => hrService.getEffectivePayrollPolicy(departmentId),
    staleTime: 30000,
  });
}

export function useCreatePayrollPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hrService.createPayrollPolicy,
    meta: { successMessage: 'Payroll policy created' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-policies'] });
    },
  });
}

export function useUpdatePayrollPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof hrService.updatePayrollPolicy>[1] }) =>
      hrService.updatePayrollPolicy(id, data),
    meta: { successMessage: 'Payroll policy updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-policies'] });
    },
  });
}

export function useDeletePayrollPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hrService.deletePayrollPolicy(id),
    meta: { successMessage: 'Payroll policy deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-policies'] });
    },
  });
}

export function useBulkDeletePayrollPolicies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => hrService.bulkDeletePayrollPolicies(ids),
    meta: { successMessage: 'Payroll policies deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-policies'] });
    },
  });
}
